import React, { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Flex,
  HStack,
  Icon,
  Image,
  SimpleGrid,
  Stack,
  Text,
  Wrap
} from '@chakra-ui/react';
import { FaCalendarAlt, FaUser } from 'react-icons/fa';
import { HiPhotograph } from 'react-icons/hi';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../api';
import PageLayout from './PageLayout';

const meses = [
  'Janeiro', 'Fevereiro', 'Marco', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

const parseLocalDate = (raw) => {
  if (!raw) return null;
  if (raw instanceof Date) return raw;
  const value = String(raw);
  if (value.includes("/")) {
    const [day, month, year] = value.split("/");
    if (year && month && day) {
      return new Date(Number(year), Number(month) - 1, Number(day));
    }
  }
  if (value.includes("-")) {
    const [year, month, day] = value.split("T")[0].split("-");
    if (year && month && day) {
      return new Date(Number(year), Number(month) - 1, Number(day));
    }
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
};


const IniciacaoPage = () => {
  const [membros, setMembros] = useState([]);
  const [mesSelecionado, setMesSelecionado] = useState(new Date().getMonth() + 1);
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`${API_BASE}/getMembros?id_pessoa=0&id_insituicao=0`)
      .then(response => response.json())
      .then(data => {
        const sortedMembros = data.result[0].sort((a, b) => {
          const dateA = parseLocalDate(a.data_iniciacao);
          const dateB = parseLocalDate(b.data_iniciacao);
          if (dateA && dateB) {
            return dateA.getTime() - dateB.getTime();
          }
          if (dateA) return -1;
          if (dateB) return 1;
          return 0;
        });
        setMembros(sortedMembros);
      })
      .catch(error => console.error('Error fetching data:', error));
  }, []);

  const handleHomeClick = () => {
    navigate('/');
  };

  const membrosFiltrados = useMemo(() => {
    return membros.filter(membro => {
      const dataIniciacao = parseLocalDate(membro.data_iniciacao);
      return dataIniciacao && dataIniciacao.getMonth() + 1 === mesSelecionado;
    });
  }, [membros, mesSelecionado]);

  return (
    <PageLayout
      title="Datas de Iniciação"
      subtitle="Iniciações"
      onBack={handleHomeClick}
      accent="#93C8E6"
      background="#EEF3F7"
      watermark="/maconaria.png"
    >
      <Stack spacing={5}>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <Box bg="white" borderRadius="22px" p={5} boxShadow="0 12px 30px rgba(15,23,42,0.22)">
            <HStack spacing={3} mb={3}>
              <Box
                width="44px"
                height="44px"
                borderRadius="14px"
                bg="rgba(147,200,230,0.5)"
                display="flex"
                alignItems="center"
                justifyContent="center"
                color="blue.700"
              >
                <Icon as={FaCalendarAlt} />
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500">Mes selecionado</Text>
                <Text fontWeight="bold" color="gray.800">{meses[mesSelecionado - 1]}</Text>
              </Box>
            </HStack>
            <Wrap spacing={2}>
              {meses.map((mes, index) => {
                const value = index + 1;
                const isSelected = value === mesSelecionado;
                return (
                  <Box
                    key={mes}
                    as="button"
                    onClick={() => setMesSelecionado(value)}
                    px={3}
                    py={2}
                    borderRadius="12px"
                    border="1px solid"
                    borderColor={isSelected ? 'blue.400' : 'gray.200'}
                    bg={isSelected ? 'blue.50' : 'white'}
                    color="gray.800"
                    boxShadow={isSelected ? '0 8px 18px rgba(15,23,42,0.12)' : 'none'}
                    _hover={{ borderColor: 'blue.300', bg: 'blue.50' }}
                    display="flex"
                    alignItems="center"
                    gap="6px"
                  >
                    <Icon as={FaCalendarAlt} color={isSelected ? 'blue.600' : 'gray.500'} />
                    <Text fontWeight={isSelected ? 'bold' : 'medium'}>{mes.slice(0, 3)}</Text>
                  </Box>
                );
              })}
            </Wrap>
          </Box>

          <Box bg="white" borderRadius="22px" p={5} boxShadow="0 12px 30px rgba(15,23,42,0.22)">
            <Text fontSize="sm" color="gray.500">Total no mes</Text>
            <Text fontSize="3xl" fontWeight="bold" color="gray.800">{membrosFiltrados.length}</Text>
            <Badge colorScheme="blue" variant="subtle" mt={2}>Iniciacoes listadas</Badge>
          </Box>
        </SimpleGrid>

        <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={5}>
          {membrosFiltrados.map((membro) => {
            const dataIniciacao = parseLocalDate(membro.data_iniciacao);
            return (
            <Box
              key={membro.id_pessoa}
              bg="white"
              borderRadius="22px"
              p={4}
              boxShadow="0 14px 30px rgba(15,23,42,0.22)"
            >
              <HStack spacing={4} align="center">
                <Box
                  bg="rgba(147,200,230,0.5)"
                  borderRadius="18px"
                  p={2}
                >
                  <Image
                    src={`${API_BASE}/Fotos/${membro.foto}.png`}
                    alt={membro.nome}
                    boxSize="64px"
                    borderRadius="16px"
                    objectFit="cover"
                  />
                </Box>
                <Box flex="1">
                  <Text fontWeight="bold" color="gray.800">{membro.nome}</Text>
                  <HStack spacing={3} mt={1} color="gray.500">
                    <Icon as={FaUser} />
                    <Text fontSize="sm">Iniciado em</Text>
                  </HStack>
                  <Text fontSize="sm" color="gray.600" mt={1}>
                    <Icon as={HiPhotograph} mr={2} />
                    {dataIniciacao
                      ? dataIniciacao.toLocaleDateString('pt-BR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric'
                        })
                      : 'Data não informada'}
                  </Text>
                </Box>
              </HStack>
            </Box>
          );
          })}
        </SimpleGrid>
      </Stack>
    </PageLayout>
  );
};

export default IniciacaoPage;
